<template>
  <div v-if="floatingReminder" class="fixed inset-x-3 top-4 z-[300] mx-auto max-w-md animate-[bounce_1s_ease-in-out_2] rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 shadow-2xl ring-4 ring-amber-200/70 sm:inset-x-auto sm:right-6 sm:top-6 sm:w-[min(26rem,calc(100vw-2rem))]" role="alertdialog" aria-live="assertive">
    <div class="flex items-start gap-3">
      <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-400 text-2xl text-white shadow-inner">⏰</div>
      <div class="min-w-0 flex-1">
        <p class="text-xs font-bold uppercase tracking-wide text-amber-800">Rappel de tâche</p>
        <p class="mt-1 break-words text-sm font-semibold text-gray-900">{{ floatingReminder.message }}</p>
        <p class="mt-1 text-xs text-amber-700">Votre tâche nécessite votre attention.</p>
      </div>
      <button type="button" class="shrink-0 text-2xl leading-none text-amber-700 hover:text-amber-900" aria-label="Fermer le rappel" @click="dismissFloatingReminder">×</button>
    </div>
    <div class="mt-3 flex justify-end gap-2">
      <button type="button" class="rounded-lg border border-amber-300 bg-white px-3 py-2 text-xs font-semibold text-amber-800 hover:bg-amber-100" @click="openReminderTask">Voir Mes tâches</button>
      <button type="button" class="rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-600" @click="dismissFloatingReminder">Fermer</button>
    </div>
  </div>

  <div class="h-screen bg-gray-50 flex overflow-hidden relative">
    <div
      v-if="mobileMenuOpen"
      class="fixed inset-0 bg-black/50 z-[70] lg:hidden"
      @click="mobileMenuOpen = false"
    ></div>

    <aside
      class="fixed lg:static inset-y-0 left-0 z-[80] w-60 bg-white border-r border-gray-200 flex flex-col transform transition-transform duration-300 ease-in-out lg:transform-none"
      :class="mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'"
    >
      <div class="h-16 flex items-center px-6 border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center space-x-2">
          <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <span class="text-lg font-semibold">AGEI</span>
        </div>
      </div>

      <nav class="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        <div v-for="category in sidebarCategories" :key="category.id">
          <button
            @click="toggleCategory(category.id)"
            class="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50 rounded-lg transition-colors"
          >
            <span>{{ category.name }}</span>
            <svg
              class="w-4 h-4 transition-transform"
              :class="{ 'rotate-180': expandedCategory === category.id }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div v-show="expandedCategory === category.id" class="mt-1 space-y-1">
            <template v-for="item in category.items" :key="item.id">
              <div v-if="item.children && item.children.length > 0" class="space-y-1">
                <button
                  @click="toggleSubmenu(item.id)"
                  class="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  :class="{ 'bg-gray-100 text-gray-900': isRouteActive(item) }"
                >
                  <div class="flex items-center space-x-3 min-w-0">
                    <component :is="iconForItem(item)" class="w-5 h-5 flex-shrink-0" />
                    <span class="truncate">{{ item.name }}</span>
                  </div>
                  <svg class="w-4 h-4 transition-transform flex-shrink-0" :class="{ 'rotate-180': expandedSubmenus[item.id] }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div v-show="expandedSubmenus[item.id]" class="ml-6 mt-1 space-y-1">
                  <router-link
                    v-for="child in item.children"
                    :key="child.id"
                    :to="child.path"
                    class="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                    :class="isRouteActive(child)
                      ? 'bg-gray-100 text-gray-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'"
                  >
                    <component :is="iconForItem(child)" class="w-4 h-4 flex-shrink-0" />
                    <span class="truncate">{{ child.name }}</span>
                  </router-link>
                </div>
              </div>

              <router-link
                v-else
                :to="item.path"
                class="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                :class="isRouteActive(item)
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'"
              >
                <component :is="iconForItem(item)" class="w-5 h-5 flex-shrink-0" />
                <span class="truncate">{{ item.name }}</span>
              </router-link>
            </template>
          </div>
        </div>
      </nav>

      <div class="border-t border-gray-200 p-4 flex-shrink-0">
        <div class="text-xs text-gray-500 mb-1">Connecté en tant que:</div>
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0 flex items-center space-x-3">
            <div class="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 overflow-hidden">
              <img
                v-if="userPhotoUrl"
                :src="userPhotoUrl"
                :alt="authStore.user?.name || 'Utilisateur'"
                class="h-full w-full object-cover"
              />
              <template v-else>
                {{ authStore.user?.name?.charAt(0) || 'N' }}
              </template>
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-gray-900" :title="authStore.user?.name || 'Utilisateur'">{{ authStore.user?.name || 'Utilisateur' }}</div>
              <div class="truncate text-xs text-gray-500">{{ authStore.user?.permission === 'ADMIN' ? 'Administrateur' : 'Utilisateur' }}</div>
            </div>
          </div>
          <button
            @click="openSettings"
            class="p-2 text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            title="Paramètres du compte"
          >
            <component :is="CogIcon" class="w-4 h-4" />
          </button>
        </div>

        <div class="mt-3">
          <button
            @click="logout"
            class="w-full flex items-center justify-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <span>Se déconnecter</span>
          </button>
        </div>
      </div>
    </aside>

    <div class="flex-1 flex flex-col overflow-hidden">
      <header class="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="lg:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Menu"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <h1 class="lg:hidden text-base font-semibold text-gray-900 truncate flex-1 mx-3">
          {{ currentPageTitle }}
        </h1>

        <div class="ml-auto flex items-center justify-end gap-3">
          <div class="relative">
            <button type="button" class="relative rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900" aria-label="Notifications" @click="notificationsOpen = !notificationsOpen">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.5-1.5A2 2 0 0118 14v-3a6 6 0 10-12 0v3a2 2 0 01-.5 1.5L4 17h5m6 0a3 3 0 01-6 0" /></svg>
              <span v-if="unreadNotificationCount" class="absolute -right-0.5 -top-0.5 min-w-4 rounded-full bg-red-600 px-1 text-center text-[10px] font-bold leading-4 text-white">{{ unreadNotificationCount > 99 ? '99+' : unreadNotificationCount }}</span>
            </button>
            <div v-if="notificationsOpen" class="absolute right-0 top-full z-[66] mt-2 w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
              <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3"><span class="font-semibold text-gray-900">Notifications</span><button v-if="unreadNotificationCount" type="button" class="text-xs text-blue-600 hover:underline" @click="markAllNotificationsRead">Tout lire</button></div>
              <div class="max-h-96 overflow-y-auto"><button v-for="notification in notifications" :key="notification.id" type="button" class="block w-full border-b border-gray-50 px-4 py-3 text-left hover:bg-gray-50" :class="!notification.readAt ? 'bg-blue-50/50' : ''" @click="markNotificationRead(notification)"><p class="text-sm font-medium text-gray-900">{{ notification.title }}</p><p class="mt-1 text-xs text-gray-600">{{ notification.message }}</p><p class="mt-1 text-[11px] text-gray-400">{{ formatNotificationDate(notification.createdAt) }}</p></button><p v-if="!notifications.length" class="px-4 py-8 text-center text-sm text-gray-500">Aucune notification.</p></div>
            </div>
          </div>
        </div>
      </header>

      <main class="flex-1 overflow-y-auto p-4 lg:p-6">
        <router-view />
      </main>
    </div>
    <MessengerWidget />
  </div>
</template>

<script setup lang="ts">
import { h, computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissionsStore, type NavigationItem } from '@/stores/permissions'
import MessengerWidget from '@/components/MessengerWidget.vue'
import { formatDateTime } from '@/utils/date'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const permissionsStore = usePermissionsStore()

const BACKEND_URL = import.meta.env.VITE_GRAPHQL_URL?.replace('/graphql', '') || 'http://localhost:4000'
const userPhotoUrl = computed(() => {
  const url = authStore.user?.photoUrl
  if (!url) return null
  const token = authStore.token
  return token ? `${BACKEND_URL}${url}?token=${encodeURIComponent(token)}` : `${BACKEND_URL}${url}`
})

const mobileMenuOpen = ref(false)
const notificationsOpen = ref(false)
const notifications = ref<any[]>([])
const unreadNotificationCount = ref(0)

const floatingReminder = ref<any | null>(null)
let notificationsInitialized = false
const seenReminderIds = new Set<number>()
let reminderAudioContext: AudioContext | null = null
let notificationSocket: WebSocket | null = null
let notificationPoll: ReturnType<typeof setInterval> | null = null
const notificationQuery = `query Notifications { notifications(limit: 30) { id type title message link readAt createdAt } unreadNotificationCount }`
const graphQLRequest = async (query: string, variables?: Record<string, unknown>) => {
  const response = await fetch(`${BACKEND_URL}/graphql`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authStore.token}` }, body: JSON.stringify({ query, variables }) })
  const payload = await response.json()
  if (payload.errors?.length) throw new Error(payload.errors[0].message)
  return payload.data
}
const playReminderAlert = () => {
  try {
    reminderAudioContext ||= new AudioContext()
    const oscillator = reminderAudioContext.createOscillator()
    const gain = reminderAudioContext.createGain()
    oscillator.type = 'sine'; oscillator.frequency.value = 880; gain.gain.value = 0.08
    oscillator.connect(gain); gain.connect(reminderAudioContext.destination)
    oscillator.start(); oscillator.stop(reminderAudioContext.currentTime + 0.35)
  } catch { /* autoplay policies may block audio until user interaction */ }
  if ('vibrate' in navigator) navigator.vibrate?.([250, 120, 250])
}
const showReminder = (notification: any) => { floatingReminder.value = notification; playReminderAlert() }
const dismissFloatingReminder = () => { floatingReminder.value = null }
const openReminderTask = () => { floatingReminder.value = null; router.push('/todo-list') }
const handleNotificationUpdate = (incoming: any[] = []) => {
  if (!notificationsInitialized) { incoming.filter(notification => notification.type === 'TODO_TASK_REMINDER').forEach(notification => seenReminderIds.add(notification.id)); notificationsInitialized = true; return }
  const reminder = incoming.find(notification => notification.type === 'TODO_TASK_REMINDER' && !notification.readAt && !seenReminderIds.has(notification.id))
  if (reminder) { seenReminderIds.add(reminder.id); showReminder(reminder) }
}
const loadNotifications = async () => {
  if (!authStore.token) return
  const data = await graphQLRequest(notificationQuery)
  notifications.value = data.notifications
  unreadNotificationCount.value = data.unreadNotificationCount
  handleNotificationUpdate(data.notifications)
}
const markNotificationRead = async (notification: any) => {
  if (!notification.readAt) { await graphQLRequest('mutation MarkNotificationRead($id: Int!) { markNotificationRead(id: $id) { id readAt } }', { id: notification.id }); await loadNotifications() }
  if (notification.link) router.push(notification.link)
  notificationsOpen.value = false
}
const markAllNotificationsRead = async () => { await graphQLRequest('mutation { markAllNotificationsRead }'); await loadNotifications() }
const formatNotificationDate = (value: string) => formatDateTime(value, { dateStyle: 'short', timeStyle: 'medium' })
const connectNotifications = () => {
  if (!authStore.token) return
  const wsUrl = BACKEND_URL.replace(/^http/, 'ws') + `/ws/notifications?token=${encodeURIComponent(authStore.token)}`
  notificationSocket = new WebSocket(wsUrl)
  notificationSocket.onmessage = () => { loadNotifications().catch(() => undefined) }
  notificationSocket.onclose = () => { notificationSocket = null }
}
onMounted(() => { loadNotifications().catch(() => undefined); connectNotifications(); notificationPoll = setInterval(() => { loadNotifications().catch(() => undefined) }, 60000) })
onBeforeUnmount(() => { notificationSocket?.close(); if (notificationPoll) clearInterval(notificationPoll) })


const expandedSubmenus = ref<Record<string, boolean>>({})

watch(() => route.path, () => {
  mobileMenuOpen.value = false
})

const GridIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' })
])
const UsersIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' })
])
const BuildingIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' })
])
const ShieldIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' })
])
const BoxIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' })
])
const TagIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' })
])
const LayersIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4' })
])
const TrendingIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z' })
])
const UserCheckIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' })
])
const ClockIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' })
])
const ClipboardIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' })
])
const DocumentIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' })
])
const CalendarIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' })
])
const CogIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' })
])
const BriefcaseIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' })
])
const LightBulbIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' })
])
const ShoppingCartIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z' })
])
const ReceiptIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2zM10 8.5a.5.5 0 11-1 0 .5.5 0 011 0zm5 5a.5.5 0 11-1 0 .5.5 0 011 0z' })
])
const UserGroupIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' })
])
const ChartBarIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' })
])
const CurrencyIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' })
])
const CreditCardIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' })
])
const CalculatorIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z' })
])
const DocumentTextIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' })
])
const TrendingUpIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' })
])
const TaskIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11' })
])
const TimesheetIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M12 8v4l3 3M3.5 6.5h17v13a2 2 0 01-2 2h-13a2 2 0 01-2-2v-13zM8 3v4M16 3v4M3.5 11h17' })
])
const WalletIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M3 8a2 2 0 012-2h13a2 2 0 012 2m-17 0v9a2 2 0 002 2h14a2 2 0 002-2v-3M3 8v13a2 2 0 002 2h14a2 2 0 002-2v-3m-18 0h18a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 01-2-2zM16 13h2' })
])
const UsersStatsIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M9 21v-2a4 4 0 014-4h2a4 4 0 014 4v2M11 11a4 4 0 100-8 4 4 0 000 8zm10 10v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75' })
])

const FolderIcon = () => h('svg', { class: 'w-5 h-5', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' }, [
  h('path', { 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'stroke-width': '2', d: 'M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z' })
])

const iconMap: Record<string, any> = {
  calendar: CalendarIcon,
  'my-leaves': ClipboardIcon,
  'my-attendance': ClockIcon,
  'bug-reports': DocumentTextIcon,
  'todo-list': TaskIcon,
  projects: FolderIcon,
  costings: CalculatorIcon,
  'my-timesheets': TimesheetIcon,
  'expense-dossiers': WalletIcon,
  'timesheets-rh': UsersStatsIcon,
  'dashboard-rh': GridIcon,
  employees: UsersIcon,
  departments: BuildingIcon,
  roles: ShieldIcon,
  attendances: ClockIcon,
  'leave-validation': UserCheckIcon,
  'logistics-dashboard': GridIcon,
  inventory: BoxIcon,
  categories: TagIcon,
  aisles: LayersIcon,
  movements: TrendingIcon,
  warranty: ShieldIcon,
  usages: UserCheckIcon,
  'sales-dashboard': ChartBarIcon,
  companies: BriefcaseIcon,
  contacts: UserGroupIcon,
  opportunities: LightBulbIcon,
  quotes: DocumentIcon,
  'purchase-orders': ShoppingCartIcon,
  'delivery-notes': ClipboardIcon,
  invoices: ReceiptIcon,
  'purchases-dashboard': ChartBarIcon,
  suppliers: BriefcaseIcon,
  'suppliers-contacts': UserGroupIcon,
  'purchase-quotes': DocumentIcon,
  'purchase-orders-suppliers': ShoppingCartIcon,
  'purchase-deliveries': ClipboardIcon,
  'purchase-invoices': ReceiptIcon,
  'accounting-dashboard': ChartBarIcon,
  'invoices-clients': ReceiptIcon,
  'supplier-invoices': DocumentTextIcon,
  payments: CreditCardIcon,
  transactions: CurrencyIcon,
  'journal-entries': DocumentIcon,
  ledger: ClipboardIcon,
  balance: CalculatorIcon,
  'financial-reports': TrendingUpIcon,
  'accounting/pcg': DocumentTextIcon,
  'accounting-settings': CogIcon,
  'accounting/periods': CalendarIcon,
  'accounting-pcg': DocumentTextIcon,
  'accounting-periods': CalendarIcon,
  'treasury-transfers': TrendingUpIcon,
  'accounting-journal': DocumentIcon,
  'journal-sales': DocumentTextIcon,
  'journal-purchases': DocumentTextIcon,
  'journal-bank': BuildingIcon,
  'accounting/taxes/tva': ReceiptIcon,
  'accounting/taxes/local': DocumentTextIcon,
  'accounting/income-statement': DocumentTextIcon,
  cashboxes: CurrencyIcon,
  'cash-journal': DocumentTextIcon,
  banks: BuildingIcon,
  'cash-approvision': CreditCardIcon,
  'treasury-operation-types': TagIcon,
  'company-info': BuildingIcon,
  settings: CogIcon,
}

const sidebarCategories = computed(() => {
  const candidate = permissionsStore.visibleSidebarTree as any
  console.log('DEBUG sidebarCategories candidate', candidate, Array.isArray(candidate), typeof candidate)
  if (!Array.isArray(candidate)) return []

  return candidate.map((category: any) => {
    if (category?.id !== 'TRESORERIE') return category
    const items = Array.isArray(category.items) ? [...category.items] : []
    const caisseIndex = items.findIndex((item: any) => item?.path === '/cashboxes')
    const banqueIndex = items.findIndex((item: any) => item?.path === '/banks')

    if (caisseIndex === -1 || banqueIndex === -1 || banqueIndex === caisseIndex + 1) {
      return category
    }

    const [banqueItem] = items.splice(banqueIndex, 1)
    items.splice(caisseIndex + 1, 0, banqueItem)

    return {
      ...category,
      items,
    }
  })
})

const flatSidebarItems = computed(() => {
  const items: NavigationItem[] = []
  const walk = (list: NavigationItem[]) => {
    for (const item of list) {
      items.push(item)
      if (item.children?.length) walk(item.children)
    }
  }
  for (const category of sidebarCategories.value) {
    walk(category.items)
  }
  return items
})

const currentPageTitle = computed(() => {
  const currentItem = flatSidebarItems.value.find(item => item.path === route.path)
  return currentItem?.name || 'AGEI'
})

const isRouteActive = (item: NavigationItem, path = route.path): boolean => {
  if (item.path && item.path === path) return true
  return Boolean(item.children?.some((child) => isRouteActive(child, path)))
}

const getCategoryFromRoute = (path: string) => {
  const categories = sidebarCategories.value ?? []
  for (const category of categories) {
    const items = Array.isArray(category?.items) ? category.items : []
    const hasMatch = items.some((item) => isRouteActive(item, path))
    if (hasMatch) return category.id
  }
  return categories[0]?.id || 'global'
}

const expandedCategory = ref('global')

watch([() => route.path, sidebarCategories], ([newPath]) => {
  const categories = sidebarCategories.value ?? []
  console.log('DEBUG watch categories', categories)
  expandedCategory.value = getCategoryFromRoute(newPath)

  const nextExpanded: Record<string, boolean> = {}
  const visit = (items: NavigationItem[]) => {
    for (const item of items) {
      if (item.children?.length) {
        nextExpanded[item.id] = isRouteActive(item, newPath)
        visit(item.children)
      }
    }
  }

  for (const category of categories) {
    const items = Array.isArray(category?.items) ? category.items : []
    visit(items)
  }

  expandedSubmenus.value = { ...expandedSubmenus.value, ...nextExpanded }
}, { immediate: true })

const toggleCategory = (categoryId: string) => {
  expandedCategory.value = categoryId
}

const toggleSubmenu = (id: string) => {
  expandedSubmenus.value = { ...expandedSubmenus.value, [id]: !expandedSubmenus.value[id] }
}

const iconForItem = (item: NavigationItem) => iconMap[item.id] || DocumentIcon

const logout = () => {
  authStore.logout()
  router.push('/login')
}

const openSettings = () => {
  router.push('/profile')
}
</script>
